import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Search,
  BookOpen,
  Database,
  AlertTriangle,
  Shield,
  DollarSign,
  Trash2,
  Sparkles,
  Settings,
  LayoutDashboard,
  Terminal,
  HelpCircle,
  Send,
  Loader2,
  ChevronDown,
  Globe,
  Tag,
  Monitor,
  ShieldCheck,
  Radar,
  Building2,
  Server,
} from "lucide-react";
import { toast } from "sonner";
import { SecureMarkdown } from "@/components/SecureMarkdown";
import remarkGfm from "remark-gfm";

const mdClasses = `prose prose-invert prose-sm max-w-none
  [&_h1]:text-lg [&_h1]:font-heading [&_h1]:text-[var(--hig-accent)] [&_h1]:mt-4 [&_h1]:mb-2
  [&_h2]:text-base [&_h2]:font-heading [&_h2]:text-[var(--hig-accent)] [&_h2]:mt-3 [&_h2]:mb-2
  [&_h3]:text-sm [&_h3]:font-heading [&_h3]:text-[var(--hig-accent)] [&_h3]:mt-2 [&_h3]:mb-1
  [&_p]:text-sm [&_p]:text-foreground/80 [&_p]:leading-relaxed
  [&_ul]:text-sm [&_ul]:text-foreground/80 [&_ol]:text-sm [&_ol]:text-foreground/80
  [&_li]:mb-1 [&_strong]:text-foreground
  [&_code]:text-[var(--hig-accent)] [&_code]:bg-[var(--hig-accent)]/10 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs
  [&_table]:border-collapse [&_table]:w-full [&_table]:text-xs
  [&_th]:text-left [&_th]:px-3 [&_th]:py-2 [&_th]:border [&_th]:border-[var(--n-border-2)] [&_th]:bg-[var(--n-bg)] [&_th]:text-[var(--hig-accent)] [&_th]:font-medium
  [&_td]:px-3 [&_td]:py-2 [&_td]:border [&_td]:border-[var(--n-border-2)] [&_td]:text-foreground/80`;

const SECTIONS = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: Radar,
    content: `### Account Setup
1. Navigate to the app URL and click **Sign up**
2. Enter your email, password, and display name
3. You'll be automatically logged in

### Adding Azure Credentials
1. Go to **Settings** from the sidebar
2. Click **Add Credential**
3. Enter your Azure Service Principal details:
   - **Display Name** — A friendly name for this subscription
   - **Tenant ID** — Your Azure AD tenant ID
   - **Client ID** — The app registration client ID
   - **Client Secret** — The app registration client secret
   - **Subscription ID** — The Azure subscription to scan
4. Click **Save** — the system validates connectivity
5. The background scanner begins discovering resources automatically

### Required Azure Permissions

| Role | Scope | Purpose |
|------|-------|---------|
| Reader | Subscription | List all resources |
| Cost Management Reader | Subscription | Access cost data |`,
  },
  {
    id: "dashboard",
    title: "Dashboard",
    icon: LayoutDashboard,
    content: `The Dashboard provides a high-level overview of your Azure estate:
- **Total resources** discovered across all subscriptions
- **Resource type breakdown** (VMs, disks, NICs, etc.)
- **Recent discovery events** (created, changed, deleted)
- **Scanner status** and last scan time`,
  },
  {
    id: "resource-explorer",
    title: "Resource Explorer",
    icon: Database,
    content: `### Browsing Resources
View all discovered resources in a paginated table: Name, Type, Resource Group, Location, Tags, Last Seen.

**Orphan Highlighting**: Orphaned resources are highlighted automatically with an amber warning icon and gold text. An **"X orphans detected"** indicator shows the total — click it to search for all orphans.

> Resources created within the last **5 business days** are excluded from orphan detection to avoid false positives.

### Search Types

**Text Search**: Type a name and press Enter to filter by name.

**Natural Language Search** (AI-powered): Ask questions in plain English.

| Query | What it does |
|-------|-------------|
| \`show orphaned resources\` | Lists all orphans with savings estimate |
| \`show orphaned snapshots\` | Filters to orphaned snapshots only |
| \`which RGs cost the most\` | Shows cost breakdown by resource group |
| \`find unused disks\` | Detects unattached disks |
| \`how much cost can I reduce\` | Estimates savings from cleanup |

### Filters
Combine filters: **Subscription**, **Location**, **Type**, **Resource Group**, **Status** (Active/Deleted).

### Resource Details Drawer
Click any row to open the side drawer:
- **Details** — Location, resource group, tags, creation date, Azure ID
- **Related** — Direct relationships (disk→VM, NIC→VM)
- **Context** — Discovery events timeline and same-RG resources`,
  },
  {
    id: "orphan-detection",
    title: "Orphan Detection",
    icon: AlertTriangle,
    content: `Niyant maps resource relationships and identifies unattached resources:

| Resource Type | Orphan Criteria |
|--------------|----------------|
| **Disks** | Not associated with any VM |
| **Snapshots** | No matching source disk or VM |
| **NICs** | Not associated with any VM |
| **NSGs** | No VM or VNet in the same resource group |
| **Public IPs** | Not associated with any VM or NIC |

### Cool-off Period
Resources created within the last **5 business days** are excluded from orphan detection. This prevents false positives on newly provisioned resources that haven't been attached yet.`,
  },
  {
    id: "cleanup-wizard",
    title: "Cleanup Wizard",
    icon: Trash2,
    content: `Access from the **sidebar**, **Resource Explorer** orphan banner, or **Cost Explorer** CTA card.

### 3-Step Wizard

**Step 1 — Review Orphans**
- View all orphans grouped by type (Disks, Snapshots, NICs, NSGs, Public IPs)
- See count cards at the top for each type
- Select individual resources or use "Select All" per type

**Step 2 — Confirm Selection**
- Review all selected resources
- Choose script type: **Azure CLI** or **PowerShell**

**Step 3 — Execute Script**
- View the AI-generated cleanup script
- **DRY_RUN is enabled by default** — only logs what it would delete
- Copy to clipboard and open Azure Cloud Shell
- Set \`DRY_RUN=false\` only after reviewing`,
  },
  {
    id: "cost-explorer",
    title: "Cost Explorer",
    icon: DollarSign,
    content: `### Viewing Costs
See spending by resource group with bar charts:
- **Total Cost** card for the selected timeframe
- **Resource Groups with Costs** count
- **Highest RG Cost** highlight

### Controls
- **Subscription filter** — scope to a specific subscription
- **Timeframe** — Last 7 / 30 / 90 days
- **Refresh** — re-fetch latest cost data

### Demo Data
If real Azure cost data isn't available, click **Generate Demo Data** to create realistic mock data based on your resource groups.

A CTA card at the bottom links to the **Cleanup Wizard** for cost reduction.`,
  },
  {
    id: "ai-insights",
    title: "AI Governance Reports",
    icon: Sparkles,
    content: `### Generating Reports
1. Go to **AI Insights** → **Governance Reports** tab
2. Click **Generate Report**
3. The AI analyzes all resources and produces:
   - **Per-Subscription Findings** — resource counts, type breakdown
   - **Governance Assessment** — tag compliance, naming conventions
   - **Security Assessment** — public exposure, NSG coverage, encryption
   - **Recommended Actions** — critical, important, and advisory items

### Managing Reports
- Click the expand arrow on any section to read details
- Click **X** to delete a single report
- Click **Clear All** to remove all reports
- Use the subscription filter to scope analysis`,
  },
  {
    id: "policy-compliance",
    title: "Azure Policy Compliance",
    icon: Shield,
    content: `### Running a Policy Scan
1. Go to **AI Insights** → **Azure Policy** tab
2. Click **Run Policy Scan**
3. The AI analyzes resources against standard Azure policies

### Policy Categories

| Category | What it checks |
|----------|---------------|
| **GDPR & Data Residency** | Data location, cross-region replication, encryption |
| **Cost Governance** | Orphaned resources, egress, over-provisioning |
| **Security & Network** | Public IP exposure, NSG rules, missing firewalls |
| **Tagging & Organization** | Required tags, naming conventions, resource locks |
| **Compute & Storage** | VM backups, managed disks, disk encryption |
| **Monitoring & Compliance** | Diagnostic settings, activity log alerts |

### Understanding Violations
Each violation card shows:
- **Severity badge** — Critical, High, Medium, or Low
- **Category** and **policy name**
- Expand to see **Impact**, **Affected Resources**, and **Remediation**

### Generating Remediation Scripts
1. Expand a violation card
2. Click **Azure CLI Script** or **PowerShell Script**
3. Review the script — **DRY_RUN is enabled by default**
4. Click **Copy** → **Azure Cloud Shell** → paste and execute
5. Set \`DRY_RUN=false\` only after reviewing`,
  },
  {
    id: "settings",
    title: "Settings",
    icon: Settings,
    content: `### Scan Settings
- **Scan Interval** — How often the background scanner runs (minutes)
- **Auto-Scan** — Enable/disable automatic scanning

### Credential Management
- View all registered Azure credentials
- Delete credentials (removes associated resources)`,
  },
  {
    id: "api-reference",
    title: "API Reference",
    icon: Terminal,
    content: `All endpoints require \`Authorization: Bearer <token>\` except auth routes.

### Authentication
\`\`\`
POST /api/auth/register   — Register new user
POST /api/auth/login      — Login, returns JWT
GET  /api/auth/me          — Get current user profile
\`\`\`

### Resources
\`\`\`
GET  /api/resources                    — List resources (paginated)
POST /api/resources/nl-query           — Natural language query
GET  /api/resources/analysis/orphan-ids — Get orphan IDs
GET  /api/resources/analysis/orphans   — Full orphan analysis
POST /api/resources/generate-script    — Generate cleanup script
\`\`\`

### Costs
\`\`\`
GET  /api/costs            — Cost data by resource group
POST /api/costs/seed-demo  — Seed demo cost data
\`\`\`

### AI Insights
\`\`\`
GET    /api/insights                    — List governance reports
POST   /api/insights/generate           — Generate report
DELETE /api/insights/{id}               — Delete single report
POST   /api/insights/policy-scan        — Run policy scan
GET    /api/insights/policy-scans       — List policy scans
POST   /api/insights/policy-remediation — Generate remediation script
\`\`\``,
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    icon: HelpCircle,
    content: `| Issue | Cause | Solution |
|-------|-------|---------|
| No resources after scan | SP lacks Reader role | Assign Reader role at subscription scope |
| No cost data | SP lacks Cost Management Reader | Assign the role to your SP |
| 401 errors | Expired token | Re-login to get a new token |
| No orphans detected | All resources have associations or created < 5 days | Wait for cool-off period |
| AI generation fails | LLM key not configured | Contact admin to configure the AI key |
| Slow scans | Large subscription (5000+ resources) | Scans may take 30-60 seconds |`,
  },
  {
    id: "enterprise-data-plane",
    title: "Enterprise Data Plane",
    icon: Building2,
    content: `### Overview
Enterprise customers can deploy their own **Data Plane** for complete data sovereignty while using the Niyant Control Plane for the application interface.

### Architecture
\`\`\`
┌─────────────────────────┐     ┌─────────────────────────┐
│   NIYANT CONTROL PLANE  │     │  YOUR DATA PLANE        │
│   (Managed by Niyant)   │◄───►│  (Your Subscription)    │
├─────────────────────────┤     ├─────────────────────────┤
│  • Web App & API        │     │  • Cosmos DB            │
│  • Authentication       │     │  • Key Vault            │
│  • Platform Secrets     │     │  • VNet (isolated)      │
└─────────────────────────┘     └─────────────────────────┘
        Your data stays in YOUR Azure subscription
\`\`\`

### Deployment Options

| Option | Best For | Data Location |
|--------|----------|---------------|
| **Niyant Hosted** | Quick start, small teams | Niyant's Azure |
| **Customer Hosted** | Enterprise, compliance | Your Azure subscription |

### Customer-Hosted Prerequisites

1. **Azure Subscription** with Contributor access
2. **Service Principal** with these roles:
   - Contributor (Resource Group scope)
   - Key Vault Administrator
   - Network Contributor

3. **Resource Providers** registered:
   - \`Microsoft.DocumentDB\`
   - \`Microsoft.KeyVault\`
   - \`Microsoft.Network\`

### Deployment Steps

**Step 1: Create Resource Group**
\`\`\`bash
az group create --name rg-niyant-data-eastus --location eastus
\`\`\`

**Step 2: Deploy via Niyant**
1. Go to **Settings** → **Deployments** → **New**
2. Select **Customer Hosted Data Plane**
3. Download ARM template or deploy directly

**Step 3: Collect Outputs**
After deployment, collect:
- Cosmos DB Connection String
- Key Vault URL
- VNet ID

\`\`\`bash
# Get Cosmos DB connection string
az cosmosdb keys list --name <COSMOS_NAME> \\
  --resource-group <RG_NAME> --type connection-strings \\
  --query "connectionStrings[0].connectionString" -o tsv
\`\`\`

**Step 4: Register with Niyant**
1. Go to **Settings** → **Data Planes** → **Register**
2. Enter collected values
3. Click **Validate & Register**

### Security Best Practices

| Setting | Recommendation |
|---------|----------------|
| **Network** | Enable Private Endpoints |
| **Key Vault** | Use RBAC, enable soft delete |
| **Cosmos DB** | Use RBAC over connection strings |
| **Firewall** | Restrict to Niyant Control Plane IPs |

### Troubleshooting

| Issue | Solution |
|-------|----------|
| Registration fails | Verify connection string format |
| Network timeout | Configure VNet peering |
| Auth error | Check tenant ID matches |

For detailed steps, see the full [Enterprise Data Plane Guide](/docs/ENTERPRISE_DATA_PLANE_GUIDE.md).`,
  },
  {
    id: "platform-setup",
    title: "Platform Setup (Admin)",
    icon: Server,
    content: `### One-Time Platform Configuration
These steps are for **Niyant administrators** setting up a new Control Plane deployment.

### Required Secrets in Key Vault

| Secret | Purpose | Generation |
|--------|---------|------------|
| \`JWT-PRIVATE-KEY\` | Sign authentication tokens | RSA 2048-bit (RS256) |
| \`JWT-PUBLIC-KEY\` | Verify authentication tokens | Derived from private key |
| \`CREDENTIAL-MASTER-KEY\` | Encrypt stored Azure credentials | 256-bit random hex |
| \`MONGO-URL\` | Database connection | From Cosmos DB |

### Generate JWT Keys

\`\`\`bash
# Generate RS256 key pair
openssl genrsa -out jwt_private.pem 2048
openssl rsa -in jwt_private.pem -pubout -out jwt_public.pem

# Store in Key Vault
az keyvault secret set --vault-name "<KV_NAME>" \\
  --name "JWT-PRIVATE-KEY" --file jwt_private.pem

az keyvault secret set --vault-name "<KV_NAME>" \\
  --name "JWT-PUBLIC-KEY" --file jwt_public.pem
\`\`\`

### Generate Credential Master Key

\`\`\`bash
MASTER_KEY=$(openssl rand -hex 32)
az keyvault secret set --vault-name "<KV_NAME>" \\
  --name "CREDENTIAL-MASTER-KEY" --value "$MASTER_KEY"
\`\`\`

### Store Cosmos DB Connection String

\`\`\`bash
# Get from Data Plane Cosmos DB
MONGO_URL=$(az cosmosdb keys list \\
  --name <COSMOS_NAME> --resource-group <DATA_RG> \\
  --type connection-strings \\
  --query "connectionStrings[0].connectionString" -o tsv)

# Store in Control Plane Key Vault
az keyvault secret set --vault-name "<CONTROL_KV>" \\
  --name "MONGO-URL" --value "$MONGO_URL"
\`\`\`

### App Service Configuration

These settings are auto-configured via ARM template but can be verified:

| Setting | Value |
|---------|-------|
| \`PYTHONPATH\` | \`/home/site/wwwroot/.python_packages/lib/site-packages\` |
| \`CORS_ORIGINS\` | \`https://app.niyant.ai\` |
| \`DB_NAME\` | \`niyant\` |
| \`AZURE_KEYVAULT_URL\` | \`https://<kv-name>.vault.azure.net/\` |

### Key Vault References

App settings can reference Key Vault secrets directly:
\`\`\`
JWT_PRIVATE_KEY=@Microsoft.KeyVault(SecretUri=https://<kv>.vault.azure.net/secrets/JWT-PRIVATE-KEY/)
\`\`\`

### Create Admin User

Use the admin creation script for production:
\`\`\`bash
python scripts/create_niyant_admin.py \\
  --db-url "mongodb://..." \\
  --email admin@company.com \\
  --name "Admin User"
\`\`\`

The script validates password requirements:
- Minimum 12 characters
- Uppercase, lowercase, digit, special character`,
  },
];

function AiSearchPanel({ api }) {
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const inputRef = useRef(null);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    try {
      const res = await api().post("/docs/search", { query: query.trim() });
      setResults((prev) => [{ query: res.data.query, answer: res.data.answer }, ...prev]);
      setQuery("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="ai-search-panel">
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ask anything about Niyant... e.g. 'How do I add credentials?' or 'What is the cool-off period?'"
            className="w-full h-10 bg-[var(--n-card)] border border-[var(--n-border-2)] rounded-lg pl-10 pr-4 text-sm placeholder:text-muted-foreground focus:outline-none focus:border-[var(--hig-accent)]/40 focus:ring-1 focus:ring-[var(--hig-accent)]/20 transition-colors"
            data-testid="docs-search-input"
          />
        </div>
        <Button
          type="submit"
          disabled={searching || !query.trim()}
          className="bg-[var(--hig-accent)] hover:opacity-90 text-primary-foreground font-semibold shrink-0"
          data-testid="docs-search-btn"
        >
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>

      {results.length > 0 && (
        <div className="space-y-3">
          {results.map((r, i) => (
            <Card key={i} className="bg-[var(--n-card)] border-[var(--n-border-2)] animate-fade-in">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-[var(--hig-accent)] shrink-0" />
                  <span className="text-xs text-[var(--hig-accent)] font-medium">"{r.query}"</span>
                </div>
                <div className={mdClasses}>
                  <SecureMarkdown content={r.answer} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DocsPage() {
  const { api } = useAuth();
  const [activeSection, setActiveSection] = useState(null);

  return (
    <div className="space-y-6" data-testid="docs-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-heading">
          Documentation
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Everything you need to know about Niyant
        </p>
      </div>

      {/* AI Search */}
      <Card className="bg-[var(--n-surface)] border-[var(--hig-accent)]/20">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-[var(--hig-accent)]" />
            <span className="text-sm font-medium">AI-Powered Search</span>
            <Badge variant="outline" className="text-[10px] border-[var(--hig-accent)]/20 text-[var(--hig-accent)]">
              Gemini
            </Badge>
          </div>
          <AiSearchPanel api={api} />
        </CardContent>
      </Card>

      {/* Doc Sections */}
      <div className="space-y-2">
        {SECTIONS.map((section) => {
          const isActive = activeSection === section.id;
          const Icon = section.icon;
          return (
            <div key={section.id} data-testid={`doc-section-${section.id}`}>
              <button
                onClick={() => setActiveSection(isActive ? null : section.id)}
                className={`w-full flex items-center justify-between p-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? "bg-[var(--hig-accent)]/5 border border-[var(--hig-accent)]/20"
                    : "bg-[var(--n-card)] border border-[var(--n-border-2)] hover:border-[#2a2a30]"
                }`}
                data-testid={`doc-toggle-${section.id}`}
              >
                <div className="flex items-center gap-3">
                  <Icon className={`w-4 h-4 ${isActive ? "text-[var(--hig-accent)]" : "text-muted-foreground"}`} />
                  <span className={`text-sm font-medium ${isActive ? "text-[var(--hig-accent)]" : ""}`}>
                    {section.title}
                  </span>
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${
                    isActive ? "rotate-180" : ""
                  }`}
                />
              </button>
              {isActive && (
                <div className="mt-1 ml-4 pl-4 border-l-2 border-[var(--hig-accent)]/10 pb-2 animate-fade-in">
                  <SecureMarkdown content={section.content} className={mdClasses} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
